import { useCallback, useEffect, useRef } from 'react';
import CardType from '@alltrails/amplitude/enums/CardType';
import ExploreMapTrailCardSortingActionTaken from '@alltrails/amplitude/enums/ExploreMapTrailCardSortingActionTaken';
import FilterName from '@alltrails/amplitude/enums/FilterName';
import MapOverlay from '@alltrails/amplitude/enums/MapOverlay';
import logExploreCardViewed from '@alltrails/amplitude/events/logExploreCardViewed';
import logExploreResultsReturned from '@alltrails/amplitude/events/logExploreResultsReturned';
import type ExploreServiceFilters from '@alltrails/explore-service/types/ExploreServiceFilters';
import type ExploreServiceSort from '@alltrails/explore-service/types/ExploreServiceSort';
import useMap from '@alltrails/maps/hooks/useMap';
import { getMapBaseLayer, getMapOverlay } from '@alltrails/maps/utils/amplitudeHelpers';
import { useAppSelector } from './redux';

export const getSortingAction = (sort: ExploreServiceSort): ExploreMapTrailCardSortingActionTaken => {
  switch (sort) {
    case 'closest':
      return 'closest';
    case 'most_popular':
      return 'most popular';
    case 'newly_added':
      return 'newly added';
    case 'seasonal':
      return 'seasonal';
    case 'best_match':
    default:
      return 'best match';
  }
};

export const getFilterNames = (
  exploreServiceFilters: ExploreServiceFilters,
  hasDistanceAwayFilter: boolean,
  hasTrailEngagementFilter: boolean
): FilterName[] => {
  const filterNames: FilterName[] = [];
  if (exploreServiceFilters.activity) {
    filterNames.push('activity');
  }
  if (exploreServiceFilters.difficulty) {
    filterNames.push('difficulty');
  }
  if (exploreServiceFilters.elevationGain) {
    filterNames.push('elevation-gain');
  }
  if (exploreServiceFilters.feature) {
    filterNames.push('attractions');
  }
  if (exploreServiceFilters.length) {
    filterNames.push('length');
  }
  if (exploreServiceFilters.minRating) {
    filterNames.push('rating');
  }
  if (exploreServiceFilters.routeType) {
    filterNames.push('route-type');
  }
  if (exploreServiceFilters.suitability) {
    filterNames.push('suitability');
  }
  if (exploreServiceFilters.trailTraffic) {
    filterNames.push('trail-traffic');
  }

  if (hasDistanceAwayFilter) {
    filterNames.push('distance-away');
  }

  if (hasTrailEngagementFilter) {
    filterNames.push('trail-completion');
  }

  return filterNames;
};

export const useLogExploreCardViewed = (cardType: CardType) => {
  const map = useMap();
  const {
    exploreViewType,
    distanceAwayIsochrone,
    exploreServiceFilters,
    searchResultsSummary,
    trailEngagement,
    baseStyleId,
    dynamicStyleIds,
    staticStyleIds
  } = useAppSelector(state => ({
    exploreViewType: state.explore.exploreViewType,
    distanceAwayIsochrone: state.exploreService.distanceAwayIsochrone,
    exploreServiceFilters: state.exploreService.filters,
    searchResultsSummary: state.exploreService.searchResultsSummary,
    trailEngagement: state.exploreService.trailEngagement,
    baseStyleId: state.map.baseStyleId,
    dynamicStyleIds: state.map.dynamicStyleIds,
    staticStyleIds: state.map.staticStyleIds
  }));

  const logEvent = useCallback(
    (id: number, index: number) => {
      if (exploreViewType) {
        logExploreCardViewed({
          bearing: map?.getBearing(),
          card_id: id,
          card_index: index,
          card_type: cardType,
          coordinates: map ? [map.getCenter().lng, map.getCenter().lat] : undefined,
          explore_view_type: exploreViewType,
          filters_applied: getFilterNames(exploreServiceFilters, Boolean(distanceAwayIsochrone), Boolean(trailEngagement.length)),
          map_base_layer: getMapBaseLayer(baseStyleId),
          map_overlays: [...dynamicStyleIds, ...staticStyleIds].map(id => getMapOverlay(id) as MapOverlay),
          num_results_returned: searchResultsSummary?.count || 0,
          pitch: map?.getPitch(),
          zoom_level: map?.getZoom()
        });
      }
    },
    [
      baseStyleId,
      cardType,
      distanceAwayIsochrone,
      dynamicStyleIds,
      exploreServiceFilters,
      exploreViewType,
      map,
      staticStyleIds,
      searchResultsSummary?.count,
      trailEngagement.length
    ]
  );

  return logEvent;
};

export const useLogExploreResultsReturned = () => {
  const map = useMap();
  const {
    exploreViewType,
    distanceAwayIsochrone,
    exploreActionType,
    exploreServiceFilters,
    trailEngagement,
    baseStyleId,
    dynamicStyleIds,
    renderedPOIs,
    searchResults,
    staticStyleIds
  } = useAppSelector(state => ({
    exploreViewType: state.explore.exploreViewType,
    distanceAwayIsochrone: state.exploreService.distanceAwayIsochrone,
    exploreActionType: state.exploreService.exploreActionType,
    exploreServiceFilters: state.exploreService.filters,
    trailEngagement: state.exploreService.trailEngagement,
    baseStyleId: state.map.baseStyleId,
    dynamicStyleIds: state.map.dynamicStyleIds,
    renderedPOIs: state.map.renderedPOIs,
    searchResults: state.exploreService.searchResults,
    staticStyleIds: state.map.staticStyleIds
  }));
  const hasNewResults = useRef(false);

  useEffect(() => {
    if (exploreViewType) {
      hasNewResults.current = true;
    }
    // Only want to call this when we get new results
  }, [searchResults]);

  useEffect(() => {
    if (hasNewResults.current && exploreViewType) {
      // When there are both a new set of results & those results have rendered on the map, log the results returned
      logExploreResultsReturned({
        action: exploreActionType || 'default landing',
        bearing: map?.getBearing(),
        coordinates: map ? [map.getCenter().lng, map.getCenter().lat] : undefined,
        explore_view_type: exploreViewType,
        filters_applied: getFilterNames(exploreServiceFilters, Boolean(distanceAwayIsochrone), Boolean(trailEngagement.length)),
        map_base_layer: getMapBaseLayer(baseStyleId),
        map_overlays: [...dynamicStyleIds, ...staticStyleIds].map(id => getMapOverlay(id) as MapOverlay),
        num_pois: renderedPOIs?.count ?? 0,
        num_results_returned: searchResults.length,
        pitch: map?.getPitch(),
        zoom_level: map?.getZoom()
      });
      hasNewResults.current = false;
    }
    // Only want to call this when pois are rendered on the map
  }, [renderedPOIs]);
};
